import type { ActivityItem, Comment } from "@gjwtrack/domain";
import { ChevronDown, Clock, Hourglass, MessageSquare, Pencil, X } from "lucide-react";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { client, useClient } from "../lib/client.ts";
import { diffText } from "../lib/diff.ts";
import { absTime, relTimeAgo, userName } from "../lib/format.ts";
import { Menu } from "../ui/Menu.tsx";
import { Avatar } from "./Badges.tsx";
import { MarkdownEditor } from "./MarkdownEditor.tsx";
import { MarkdownView } from "./MarkdownView.tsx";

/** Consecutive changes by the same person within a minute show under one header, as YouTrack groups them. */
const GROUP_WINDOW_MS = 60_000;

// ----------------------------------------------------------------- settings (per browser, like YouTrack's "Activity settings")

interface Settings {
  sort: "oldest" | "newest";
  expandComments: boolean;
  showComments: boolean;
  showHistory: boolean;
}
const DEFAULTS: Settings = { sort: "newest", expandComments: true, showComments: true, showHistory: true };
const KEY = "gjw.activity";
const readSettings = (): Settings => {
  try {
    return { ...DEFAULTS, ...(JSON.parse(localStorage.getItem(KEY) ?? "{}") as Partial<Settings>) };
  } catch {
    return DEFAULTS;
  }
};
const writeSettings = (s: Settings): void => {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    /* private mode */
  }
};

// ----------------------------------------------------------------- change lines

interface Group {
  actorId: number | null;
  at: number;
  items: ActivityItem[];
}

function groupItems(items: ActivityItem[]): Group[] {
  const groups: Group[] = [];
  for (const it of items) {
    const last = groups[groups.length - 1];
    if (last && last.actorId === it.actorId && it.at - last.at <= GROUP_WINDOW_MS) last.items.push(it);
    else groups.push({ actorId: it.actorId, at: it.at, items: [it] });
  }
  return groups;
}

const Values = ({ v }: { v: string[] }) => <span className={v.length ? "text-ink" : "text-faint"}>{v.length ? v.join(", ") : "—"}</span>;

/** `old → new` for single values; `+added −removed` lists for multi-value fields and for sets (tags, attachments, links). */
function Change({ label, item, set = false }: { label: string; item: ActivityItem; set?: boolean }) {
  const single = !set && item.added.length <= 1 && item.removed.length <= 1;
  // Long texts (summaries) read better stacked than inline around the arrow.
  const stacked = single && [...item.added, ...item.removed].some((v) => v.length > 48);
  if (stacked) {
    return (
      <li>
        <span className="text-muted">{label}:</span>
        <div className="mt-0.5 ml-3 border-l-2 border-line pl-2">
          <div className="text-muted line-through decoration-faint">{item.removed[0] ?? "—"}</div>
          <div>{item.added[0] ?? "—"}</div>
        </div>
      </li>
    );
  }
  return (
    <li className="flex flex-wrap items-baseline gap-x-1.5">
      <span className="text-muted">{label}:</span>
      {single ? (
        <>
          <Values v={item.removed} />
          <span className="text-faint">→</span>
          <Values v={item.added} />
        </>
      ) : (
        <>
          {item.added.map((v) => (
            <span key={`+${v}`} className="text-ok">
              +{v}
            </span>
          ))}
          {item.removed.map((v) => (
            <span key={`-${v}`} className="text-danger">
              −{v}
            </span>
          ))}
        </>
      )}
    </li>
  );
}

/** "Description changed" with YouTrack's markup: deleted text red and struck through, inserted text green, inside the full new text. */
function DescriptionChange({ item }: { item: ActivityItem }) {
  const [open, setOpen] = useState(true);
  const before = item.removed[0] ?? "";
  const after = item.added[0] ?? "";
  const lines = useMemo(() => (open ? diffText(before, after) : []), [open, before, after]);
  return (
    <li>
      <button className="text-muted hover:underline" onClick={() => setOpen((o) => !o)}>
        Description {item.removed.length ? "changed" : "added"}
        <span className="ml-1 text-faint">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="diff mt-1 max-h-96 overflow-auto rounded border border-line bg-sunken p-2 text-[13px] leading-relaxed whitespace-pre-wrap">
          {lines.map((l, i) => (
            <div key={i} className={l.type === "add" ? "diff-add-line" : l.type === "del" ? "diff-del-line" : ""}>
              {l.spans.length === 0 ? "\u00a0" : l.spans.map((sp, j) => (sp.type === "same" ? <span key={j}>{sp.value}</span> : <span key={j} className={sp.type === "add" ? "diff-add" : "diff-del"}>{sp.value}</span>))}
            </div>
          ))}
        </div>
      )}
    </li>
  );
}

function Line({ item }: { item: ActivityItem }) {
  switch (item.kind) {
    case "created":
      return <li className="text-muted">Created the issue</li>;
    case "summary":
      return <Change label="Summary" item={item} />;
    case "description":
      return <DescriptionChange item={item} />;
    case "field":
      return <Change label={item.field ?? "Field"} item={item} />;
    case "tags":
      return <Change label="Tags" item={item} set />;
    case "links":
      return <Change label={item.field ? `Link (${item.field})` : "Links"} item={item} set />;
    case "attachments":
      return <Change label="Attachments" item={item} set />;
    case "project":
      return <Change label="Project" item={item} />;
    case "visibility":
      return <Change label="Visibility" item={item} />;
  }
}

/** One group of changes: YouTrack's clock badge on the left, "Name · Updated 1 day ago", then the lines. */
function HistoryEntry({ g }: { g: Group }) {
  const schema = useClient((s) => s.schema);
  const created = g.items.length === 1 && g.items[0]!.kind === "created";
  return (
    <li className="flex gap-3" data-test="activity-entry">
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded border border-line text-muted" aria-hidden>
        <Clock size={14} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center gap-2 text-[13px] text-muted">
          <b className="text-value">{g.actorId !== null ? userName(schema, g.actorId) : "System"}</b>
          <span className="text-faint">•</span>
          <span title={absTime(g.at)}>
            {created ? "Created" : "Updated"} {relTimeAgo(g.at)}
          </span>
        </div>
        {!created && (
          <ul className="flex flex-col gap-0.5 text-[13.5px]">
            {g.items.map((it) => (
              <Line key={it.id} item={it} />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

// ----------------------------------------------------------------- comments

function CommentItem({ issueId, comment: c, mine, isAdmin, expanded }: { issueId: number; comment: Comment; mine: boolean; isAdmin: boolean; expanded: boolean }) {
  const schema = useClient((s) => s.schema);
  const [editing, setEditing] = useState(false);
  const [open, setOpen] = useState(false);
  const collapsed = !expanded && !open && c.text.length > 300;
  return (
    <li className={`group flex gap-3 ${c.id < 0 ? "opacity-60" : ""}`} data-test="comment-entry">
      <Avatar schema={schema} userId={c.authorId} size={28} />
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex items-center gap-2 text-[13px] text-muted">
          <b className="text-value">{userName(schema, c.authorId)}</b>
          <span className="text-faint">•</span>
          <span title={absTime(c.createdAt)}>{c.id < 0 ? "sending…" : `commented ${relTimeAgo(c.createdAt)}`}</span>
          {c.updatedAt > c.createdAt + 1000 && <span>(edited)</span>}
          {(mine || isAdmin) && c.id > 0 && !editing && (
            <span className="ml-auto flex gap-1 opacity-0 group-hover:opacity-100">
              <button className="icon-btn h-6 w-6" title="Edit" onClick={() => setEditing(true)}>
                <Pencil size={14} />
              </button>
              <button className="icon-btn h-6 w-6" title="Delete" onClick={() => confirm("Delete this comment?") && void client.deleteComment(issueId, c.id)}>
                <X size={14} />
              </button>
            </span>
          )}
        </div>
        {editing ? (
          <MarkdownEditor
            initial={c.text}
            compact
            autoFocus
            target={{ issueId }}
            onSave={async (md) => {
              setEditing(false);
              if (md && md !== c.text) await client.editComment(issueId, c.id, md).catch(() => {});
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <div className={collapsed ? "relative max-h-24 overflow-hidden" : ""}>
            <MarkdownView text={c.text} target={{ issueId }} className="text-[14.5px]" />
            {collapsed && (
              <button className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ground pt-8 text-left text-[13px] text-value hover:underline" onClick={() => setOpen(true)}>
                Show more
              </button>
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function Composer({ issueId }: { issueId: number }) {
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const [composing, setComposing] = useState(false);
  if (!schema || !me) return null;
  return (
    <div className="mb-5 flex gap-3">
      <Avatar schema={schema} userId={me.id} size={28} />
      <div className="min-w-0 flex-1">
        {composing ? (
          <MarkdownEditor
            initial=""
            placeholder="Write a comment…"
            saveLabel="Comment"
            autoFocus
            compact
            target={{ issueId }}
            onSave={async (md) => {
              if (!md) return;
              await client.addComment(issueId, md).catch(() => {});
              setComposing(false);
            }}
            onCancel={() => setComposing(false)}
          />
        ) : (
          // YouTrack's empty comment box is a few lines tall, placeholder at the top-left; same height as the editor it opens into.
          <button className="field flex w-full items-start px-3 py-2 text-left text-faint" style={{ minHeight: "5rem" }} onClick={() => setComposing(true)}>
            Write a comment…
          </button>
        )}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- the stream

type Entry = { at: number; key: string; comment: Comment } | { at: number; key: string; group: Group };

function Toggle({ on, title, disabled = false, onClick, children }: { on: boolean; title: string; disabled?: boolean; onClick?: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      className={`flex h-8 w-9 items-center justify-center border-t-2 ${on ? "border-accent text-accent" : "border-transparent text-muted"} ${disabled ? "cursor-default opacity-40" : "hover:text-ink"}`}
      title={title}
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
    >
      {children}
    </button>
  );
}

/**
 * YouTrack's activity stream: comments and history in one list, three toggles on the left
 * (comments, work items — not built yet, history), "Activity settings" on the right for
 * sort order and comment expansion. Settings persist per browser.
 */
export function ActivityStream({ issueId, canComment }: { issueId: number; canComment: boolean }) {
  const comments = useClient((s) => s.comments[issueId]);
  const activity = useClient((s) => s.activity[issueId]);
  const me = useClient((s) => s.me);
  const [settings, setSettings] = useState<Settings>(readSettings);
  const update = (patch: Partial<Settings>): void =>
    setSettings((s) => {
      const next = { ...s, ...patch };
      writeSettings(next);
      return next;
    });

  useEffect(() => {
    void client.loadActivity(issueId);
    return () => client.forgetActivity(issueId);
  }, [issueId]);

  const entries = useMemo(() => {
    const out: Entry[] = [];
    if (settings.showComments) for (const c of comments?.items ?? []) out.push({ at: c.createdAt, key: `c${c.id}`, comment: c });
    if (settings.showHistory) for (const g of groupItems(activity?.items ?? [])) out.push({ at: g.at, key: g.items[0]!.id, group: g });
    out.sort((x, y) => x.at - y.at || x.key.localeCompare(y.key));
    return settings.sort === "newest" ? out.reverse() : out;
  }, [comments?.items, activity?.items, settings.showComments, settings.showHistory, settings.sort]);

  const ready = (!settings.showComments || comments?.status === "ready") && (!settings.showHistory || activity?.status === "ready");
  const count = (settings.showComments ? (comments?.items.length ?? 0) : 0) + (settings.showHistory ? (activity?.items.length ?? 0) : 0);

  return (
    <section className="mt-6 ml-10" data-test="activity">
      <div className="mb-3 flex items-center border-b border-line">
        <Toggle on={settings.showComments} title="Comments" onClick={() => update({ showComments: !settings.showComments })}>
          <MessageSquare size={16} />
        </Toggle>
        <Toggle on={false} title="Work items (not available yet)" disabled>
          <Hourglass size={16} />
        </Toggle>
        <Toggle on={settings.showHistory} title="History" onClick={() => update({ showHistory: !settings.showHistory })}>
          <Clock size={16} />
        </Toggle>
        <span className="ml-3 text-[13px] text-muted tabular-nums">{count > 0 ? count : null}</span>
        {/* Ring's Dropdown wraps the anchor in its own inline-block, so the push-right lives on this wrapper. */}
        <div className="ml-auto">
          <Menu
            anchor={
              <button type="button" className="flex items-center gap-1 text-[13px] text-muted hover:text-ink" data-test="activity-settings">
                Activity settings <ChevronDown size={14} />
              </button>
            }
          items={[
            { key: "oldest", label: "Sort: oldest first", checked: settings.sort === "oldest", onClick: () => update({ sort: "oldest" }) },
            { key: "newest", label: "Sort: newest first", checked: settings.sort === "newest", onClick: () => update({ sort: "newest" }) },
            { key: "expand", label: "Expand comments", checked: settings.expandComments, separator: true, onClick: () => update({ expandComments: !settings.expandComments }) },
          ]}
          />
        </div>
      </div>
      {canComment && <Composer issueId={issueId} />}
      <ol className="flex flex-col gap-4">
        {entries.map((e) =>
          "comment" in e ? (
            <CommentItem key={e.key} issueId={issueId} comment={e.comment} mine={e.comment.authorId === me?.id} isAdmin={me?.isAdmin ?? false} expanded={settings.expandComments} />
          ) : (
            <HistoryEntry key={e.key} g={e.group} />
          ),
        )}
        {ready && entries.length === 0 && <li className="text-faint">Nothing here yet.</li>}
      </ol>
    </section>
  );
}
